import { useEffect, useState } from 'react';

import { ApiError } from '@/components/modules/_shared/exceptions/ApiError';
import { useCheckoutSession } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useCheckoutSession';
import { LenderSelectedData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { PaymentIntentDialog } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/PaymentIntent/PaymentIntentDialog';
import { BnplPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/entities/PaymentMethodDetail/BnplPaymentMethodDetailEntity';
import { useBnplPaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/hooks/useBnplPaymentIntent';
import { LenderSelected } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/PaymentIntent/components/LenderSelected';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';

interface PaymentIntentProps {
  paymentMethodDetailEntity: BnplPaymentMethodDetailEntity;
  onLenderSelected?: (data: LenderSelectedData) => void;
  onClose?: () => void;
}

export const PaymentIntentAndLenderSelected = ({ paymentMethodDetailEntity }: PaymentIntentProps) => {
  const onLenderSelected = () => {};

  const [showDialog, setShowDialog] = useState(true);

  if (!showDialog) {
    return null;
  }

  return (
    <PaymentIntentDialog>
      <PaymentIntent
        paymentMethodDetailEntity={paymentMethodDetailEntity}
        onLenderSelected={onLenderSelected}
        onClose={() => setShowDialog(false)}
      />
    </PaymentIntentDialog>
  );
};

export const PaymentIntent = ({ paymentMethodDetailEntity, onLenderSelected, onClose }: PaymentIntentProps) => {
  const { checkoutSessionEntity, refreshCheckoutSessionAndPaymentMethodEntities } = useCheckoutSession();
  const { createPaymentIntent, mountPaymentIntent } = useBnplPaymentIntent();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [lenderSelectedData, setLenderSelectedData] = useState<LenderSelectedData | null>(null);

  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  if (!checkoutSessionEntity || checkoutSessionEntity.hasFinalStatus()) {
    return null;
  }

  const openLenderSelectedDialog = (data: LenderSelectedData) => {
    setLenderSelectedData(data);
    setShowDialog(true);
    onLenderSelected?.(data);
  };

  const isExpired = paymentMethodDetailEntity.isExpired();
  const shouldCreatePaymentIntent = paymentMethodDetailEntity.shouldCreatePaymentIntent();

  const onProcess = async () => {
    if (
      !isExpired &&
      paymentMethodDetailEntity.selectedProvider &&
      paymentMethodDetailEntity.lenderRequestId &&
      paymentMethodDetailEntity.lenderApprovalState
    ) {
      openLenderSelectedDialog({
        lenderName: paymentMethodDetailEntity.selectedProvider,
        lenderApprovalState: paymentMethodDetailEntity.lenderApprovalState,
        lenderApprovalStatus: paymentMethodDetailEntity.lenderApprovalStatus,
      });
      return;
    }
    setLoading(true);
    setDisabled(true);
    try {
      await createPaymentIntent(paymentMethodDetailEntity);
    } catch (error) {
      if (error instanceof ApiError) {
        setError(error);
        setLoading(false);
        return;
      }
    }

    const onPaymentIntentClose = async () => {
      await refreshCheckoutSessionAndPaymentMethodEntities();
      onClose?.();
    };

    await mountPaymentIntent(paymentMethodDetailEntity, onPaymentIntentClose, openLenderSelectedDialog);

    setTimeout(() => {
      setDisabled(false);
      setLoading(false);
    }, 5000);
  };

  return (
    <>
      <StandardButton main size="small" loading={loading} disabled={disabled} onClick={onProcess}>
        <Text tId={shouldCreatePaymentIntent ? (isExpired ? 'RESTART' : 'START') : 'CONTINUE_APPLICATION'} />
      </StandardButton>
      <LenderSelected
        paymentMethodDetailEntity={paymentMethodDetailEntity}
        lenderSelectedData={lenderSelectedData}
        showDialog={showDialog}
        setShowDialog={setShowDialog}
        onClose={() => onClose?.()}
      />
    </>
  );
};
